import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { adminDriversApi } from "@/lib/commerce";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", vehicle: "", notes: "", is_active: true };

export default function AdminDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [editing, setEditing] = useState(null); // driver id or 'new'
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => adminDriversApi.list().then(setDrivers).catch(() => setDrivers([]));
  useEffect(() => { load(); }, []);

  const startEdit = (d) => {
    setEditing(d.id);
    setForm({
      name: d.name || "", phone: d.phone || "",
      vehicle: d.vehicle || "", notes: d.notes || "",
      is_active: d.is_active !== false,
    });
    setError("");
  };
  const startNew = () => { setEditing("new"); setForm(EMPTY); setError(""); };
  const cancel = () => { setEditing(null); setForm(EMPTY); setError(""); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (editing === "new") {
        await adminDriversApi.create(form);
        toast.success("Driver added.");
      } else {
        await adminDriversApi.update(editing, form);
        toast.success("Driver updated.");
      }
      cancel();
      load();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Save failed.");
    } finally { setBusy(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this driver?")) return;
    await adminDriversApi.deactivate(id);
    toast.success("Driver deactivated.");
    load();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-drivers-root">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Drivers</div>
            <div className="text-lg font-semibold">Delivery personnel</div>
          </div>
          <div className="flex gap-3">
            <Button asChild variant="outline"><Link to="/admin">&larr; Dashboard</Link></Button>
            {!editing && <Button onClick={startNew} data-testid="admin-driver-new">Add driver</Button>}
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        {editing && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{editing === "new" ? "New driver" : "Edit driver"}</CardTitle>
              <CardDescription>Drivers can be assigned to orders for delivery.</CardDescription>
            </CardHeader>
            <CardContent>
              {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}
              <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="admin-driver-form">
                <Field label="Name" required>
                  <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="admin-driver-name" />
                </Field>
                <Field label="Phone" required>
                  <Input required placeholder="+971…" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="admin-driver-phone" />
                </Field>
                <Field label="Vehicle">
                  <Input placeholder="Bike / Van / …" value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)} />
                </Field>
                <Field label="Notes">
                  <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                </Field>
                <label className="text-sm flex items-center gap-2 sm:col-span-2">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
                  Active (available for assignment)
                </label>
                <div className="sm:col-span-2 flex gap-2">
                  <Button type="submit" disabled={busy} data-testid="admin-driver-save">Save</Button>
                  <Button type="button" variant="ghost" onClick={cancel}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left">Vehicle</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <tr key={d.id} className="border-t" data-testid={`admin-driver-row-${d.id}`}>
                  <td className="p-3 font-medium">{d.name}</td>
                  <td className="p-3 text-xs">{d.phone}</td>
                  <td className="p-3 text-xs">{d.vehicle || "—"}</td>
                  <td className="p-3">
                    <Badge variant={d.is_active ? "secondary" : "outline"}>{d.is_active ? "active" : "inactive"}</Badge>
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(d)} data-testid={`admin-driver-edit-${d.id}`}>Edit</Button>
                    {d.is_active && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deactivate(d.id)} data-testid={`admin-driver-deactivate-${d.id}`}>
                        Deactivate
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {drivers.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">
                  No drivers added yet. Click <em>Add driver</em> to create one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="space-y-1 text-sm block">
      <span className="text-muted-foreground">{label}{required && <span className="text-destructive"> *</span>}</span>
      {children}
    </label>
  );
}
